import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { BehaviorSubject, combineLatest, filter, Observable, Subject, Subscription, switchMap, tap } from 'rxjs';
import * as uuid from 'uuid';

import { AbstractDialogComponent } from '../../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { ButtonTypesEnum } from '../../../../Forms/enums/button-types.enum';
import { EmailComposerModeEnum } from '../../../enums/email-composer-mode.enum';
import { TextInputComponent } from '../../../../Forms/components/TextInput/text-input.component';
import { RichTextEditorConfigModel } from '../../../../Forms/components/RichTextEditorCK/model/rich-text-editor-config.model';
import { NylasMessageModel } from '../../../../../Discover/Targeting/models/nylas-message.model';
import { PreferencesModel } from '../../../../../Core/Users/models/preferences.model';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { EmailConversationService } from '../../../../../Discover/Targeting/services/email-conversation.service';
import { EmailAccountConnectorMessageModel } from '../../../../../Discover/Targeting/models/email-account-connector-message.model';
import { DataConnectionService } from '../../../../../Core/Users/services/data-connection.service';
import { CustomDropdownOption, DropdownOptionModel } from '../../../../Forms/models/dropdown-option.model';
import { EmailAccountConnectorApi } from '../../../../../api/emailAccountConnector/models/email-account-connector.api';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import { AgprComponentStyleEnum } from '../../../../Basic/enums/social-analysis-types.enum';
import { DataConnectorTypesEnum } from '../../../../../api/externalData/enums/data-connector-types.enum';
import { NewDataConnectionDialogComponent } from '../../../../../Account/components/NewDataConnectionDialog/new-data-connection-dialog.component';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { UserPreferencesService } from '../../../../../Account/UserPreferences/services/user-preferences.service';
import { DistributionSendFromApi } from '../../../../../api/distributions/model/distribution-api.model';
import { TargetingRhpContextsService } from '../../../../TargetingShared/services/targeting-rhp-contexts.service';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';


@Component({
    selector: 'agpr-email-composer-overtake',
    templateUrl: './email-composer-overtake.component.html',
    styleUrls: ['./email-composer-overtake.component.scss'],
    animations: [],
    standalone: false
})
export class EmailComposerOvertakeComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly emailComposerModes = EmailComposerModeEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly localStorageKey = 'RecentNylasMessageId';

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    contactName: string;

    mode: EmailComposerModeEnum;

    @ViewChild('fromNameInput') fromNameInput: TextInputComponent;
    @ViewChild('replyEmailInput') replyEmailInput: TextInputComponent;
    @ViewChild('subjectLineInput') subjectLineInput: TextInputComponent;
    @ViewChild('previewTextInput') previewTextInput: TextInputComponent;

    hideSlideOverlay: boolean = false;

    header: string = 'Discover.emailComposer.header.conversationWith';
    messages: NylasMessageModel[] = [];
    newEmailMessage: EmailAccountConnectorMessageModel = new EmailAccountConnectorMessageModel();
    mostRecentMessage: NylasMessageModel;
    defaultTimeFormat: string;
    showRecentMessageFeedback: boolean = false;
    contactEmail: string;

    ckEditorConfig: RichTextEditorConfigModel;
    isReply: boolean = false;

    userPrefs: PreferencesModel;

    emailAccountOptions: DropdownOptionModel<EmailAccountConnectorApi>[] = [];
    selectedDefaultEmailAccount: DropdownOptionModel<EmailAccountConnectorApi>;

    sendFromCallToAction: CustomDropdownOption = {
        label: 'Discover.targeting.distribution.headerEditor.sendFrom.callToAction',
        value: null,
        action: () => this.createNewEmailAccountConnector()
    };

    expandAll: boolean = true;

    private subscriptions: Subscription[] = [];
    private accountId: number;
    private sendSubject = new Subject<void>();

    constructor(
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        public currentUserStateService: CurrentUserStateService,
        public emailConversationService: EmailConversationService,
        public dataConnectionService: DataConnectionService,
        private userPreferencesService: UserPreferencesService,
        private targetingRhpContextsService: TargetingRhpContextsService
    ) {
        super(coreDialogService, uuid.v4());
    }

    ngOnInit(): void {
        this.ckEditorConfig = new RichTextEditorConfigModel({
            aiReferenceId: this.key,
            enableAIRefinement: true,
            hideInsert: true,
        });

        this.subscriptions.push(
            this.currentUserStateService.getAccount().pipe(filter(x => !!x)).subscribe( account => {
                this.accountId = account.id;
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe( pref => {
                this.userPrefs = pref;
                this.defaultTimeFormat = this.userPrefs.getDateFormatString(true, true, true);
            }),
            this.emailConversationService.mode$.subscribe(mode => {
                this.mode = mode;
                if (this.mode === EmailComposerModeEnum.COMPOSE) {
                    this.header = 'Discover.emailComposer.header.email';
                } else {
                    this.header = 'Discover.emailComposer.header.conversationWith';
                }
            }),
            this.targetingRhpContextsService.getContact().pipe(filter(x => !!x)).subscribe(contact => {
                if (contact.firstName && contact.lastName) {
                    this.contactName = contact.firstName + ' ' + contact.lastName;
                } else if (contact.name) {
                    this.contactName = contact.name;
                } else {
                    this.contactName = contact.primaryEmail;
                }
                this.contactEmail = contact.primaryEmail;
            }),

            this.emailConversationService.messages$.subscribe(messages => {
                if (messages?.length) {
                    this.messages = messages;
                    // by default, expand the most recent email
                    this.messages[0].expanded = true;
                    const initialDistributionMessage = this.messages[this.messages.length - 1];
                    this.newEmailMessage.fromName = initialDistributionMessage.fromName;
                    this.newEmailMessage.fromEmail = initialDistributionMessage.fromEmail;
                    this.newEmailMessage.toName = initialDistributionMessage.toName;
                    this.newEmailMessage.replyTo = initialDistributionMessage.fromEmail;

                    this.mostRecentMessage = this.messages.find(message => message.fromEmail === initialDistributionMessage.toEmail);
                    this.newEmailMessage.replyMessageId = this.mostRecentMessage.messageId;
                    this.newEmailMessage.subjectLine = 'RE: ' + this.mostRecentMessage.subject;
                    this.showRecentMessageFeedback = this.emailConversationService.readMessagesFromLocalStorage(this.messages, this.accountId).length > 0;
                }
            }),
            combineLatest([
                this.dataConnectionService.emailAccountConnectors$,
                this.userPreferencesService.getUserAppPreferences()
            ]).subscribe(([connectors, userAppPreferences]) => {
                this.emailAccountOptions = [];

                connectors?.forEach(connector => {
                    this.emailAccountOptions.push({
                        label: connector.connectorName,
                        subLabel: connector.connectorEmail,
                        value: connector
                    });
                });

                // default to system default
                this.selectedDefaultEmailAccount = this.emailAccountOptions[0];

                if (userAppPreferences?.targetingPrefs?.distributionsPrefs?.defaultEmailAccount) {
                    this.emailAccountOptions.forEach((x, index) => {
                        if (x.value?.id === userAppPreferences?.targetingPrefs?.distributionsPrefs?.defaultEmailAccount) {
                            this.selectedDefaultEmailAccount = this.emailAccountOptions[index];
                        }
                    });
                }
            }),
            this.sendSubject.pipe(
                switchMap(() => {
                    // Note: In both cases, the newEmailMessage is updated with proper sendFrom/grant data
                    if (this.isReply) {
                        return this.emailConversationService.replyDistributionMessage(this.newEmailMessage).pipe(
                            tap(() => this.refresh())
                        );
                    } else {
                        if (this.selectedDefaultEmailAccount.value) {
                            this.newEmailMessage.sendFrom = {
                                connectionEmail: this.selectedDefaultEmailAccount.value.connectorEmail,
                                connectionId: this.selectedDefaultEmailAccount.value.id,
                                grantId: this.selectedDefaultEmailAccount.value.grant.id
                            } as DistributionSendFromApi;
                        }
                        return this.emailConversationService.sendEmail(this.newEmailMessage).pipe(
                            tap(() => this.closeDialog())
                        );
                    }
                }),
            ).subscribe(() => {
                this.prepareNewMessage();
                this.emailConversationService.storeMessageInLocalStorage(this.newEmailMessage, this.accountId);
                this.isReply = false;
            })
        );
    }

    prepareNewMessage() {
        this.newEmailMessage.replyMessageId = this.mostRecentMessage.messageId;
        this.newEmailMessage.subjectLine = 'RE: ' + this.mostRecentMessage.subject;
        this.newEmailMessage.bodyHtml = '';
        this.newEmailMessage.bodyPlainText = '';
    }

    refresh(): void {
        this.emailConversationService.refreshConversation();
    }

    reply(): void {
        this.isReply = true;
        this.header = 'Discover.emailComposer.header.replyTo';
    }

    send(): void {
        this.sendSubject.next();
    }

    clear(): void {
        this.newEmailMessage.bodyHtml = '';
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(x => x.unsubscribe());
    }

    closeDialog() {
        this.hideSlideOverlay = true;
        this.emailConversationService.reset();
        this.coreDialogService.close(this.key);
    }

    createNewEmailAccountConnector() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                style: AgprComponentStyleEnum.social,
                selectedConnectionType: DataConnectorTypesEnum.EMAIL_ACCOUNT
            },
            outputs: {
                dialogClose: (confirm: boolean) => {}
            }
        };

        this.coreDialogService.open(NewDataConnectionDialogComponent, io);
    }

    toggleExpand(): void {
        if (this.expandAll) {
            this.messages.forEach(message => message.expanded = true);
        } else {
            this.messages.forEach(message => message.expanded = false);
        }
        this.expandAll = !this.expandAll;
    }
}
